import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useStudentAuth, studentSupabase } from '../../contexts/StudentAuthContext';
import { getPmEventPreferredLiveUrl } from '../../lib/pmEventUtils';
import SessionTimes from '../../components/Mentors/SessionTimes';

export default function StudentDashboard() {
  const { student, loading, signOut } = useStudentAuth();
  const [rows, setRows] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(async () => {
    if (!studentSupabase || !student?.id) return;
    setBusy(true);
    setLoadError(null);
    try {
      const { data: access, error: accessErr } = await studentSupabase
        .from('mentors_event_access')
        .select('id, pm_event_id, granted_at, event:pm_event_id(id, title, slug, status, format, language)')
        .eq('mentors_user_id', student.id)
        .order('granted_at', { ascending: false });
      if (accessErr) throw accessErr;

      const eventIds = (access ?? []).map((a) => a.pm_event_id).filter(Boolean);
      let sessionsByEvent = {};
      let invitesByEvent = {};

      if (eventIds.length) {
        const [{ data: sessions, error: sessErr }, { data: invites, error: invErr }] =
          await Promise.all([
            studentSupabase
              .from('pm_event_sessions')
              .select('id, event_id, title, starts_at, ends_at, timezone, location_text, meeting_url, sort_order')
              .in('event_id', eventIds)
              .order('starts_at', { ascending: true }),
            studentSupabase
              .from('pm_event_invitation_links')
              .select('id, event_id, slug, click_count')
              .eq('mentors_user_id', student.id)
              .in('event_id', eventIds),
          ]);
        if (sessErr) throw sessErr;
        if (invErr) throw invErr;

        sessionsByEvent = (sessions ?? []).reduce((acc, s) => {
          (acc[s.event_id] ||= []).push(s);
          return acc;
        }, {});
        invitesByEvent = (invites ?? []).reduce((acc, inv) => {
          acc[inv.event_id] = inv;
          return acc;
        }, {});
      }

      setRows(
        (access ?? []).map((a) => ({
          accessId: a.id,
          event: a.event,
          sessions: sessionsByEvent[a.pm_event_id] || [],
          invite: invitesByEvent[a.pm_event_id] || null,
        })),
      );
    } catch (err) {
      setLoadError(err.message || 'Failed to load dashboard');
    } finally {
      setBusy(false);
    }
  }, [student?.id]);

  useEffect(() => {
    load();
  }, [load]);

  if (!loading && !student) {
    return <Navigate to="/student/login" replace />;
  }

  const copyInvite = async (event, invite) => {
    if (!event?.slug || !invite?.slug) return;
    const url = getPmEventPreferredLiveUrl(event, invite.slug);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(invite.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900">
      <header className="border-b border-stone-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-stone-500">Prosperity Mentors</p>
            <h1 className="text-lg font-semibold">Student dashboard</h1>
          </div>
          <div className="text-right">
            <p className="text-sm text-stone-600">{student?.full_name || student?.email}</p>
            <button
              type="button"
              onClick={() => signOut()}
              className="text-xs text-amber-800 hover:underline"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-8">
        {busy && <p className="text-sm text-stone-500">Loading…</p>}
        {loadError && <p className="text-sm text-red-600">{loadError}</p>}

        <section className="space-y-4">
          <h2 className="text-xl font-semibold">Your events</h2>
          {!busy && !rows.length && (
            <p className="text-sm text-stone-600">No event access yet. Register for an event and claim your account.</p>
          )}
          {rows.map(({ accessId, event, sessions, invite }) => (
            <article key={accessId} className="rounded-2xl border border-stone-200 bg-white p-5 space-y-3">
              <div>
                <h3 className="font-semibold text-lg">{event?.title || 'Event'}</h3>
                <p className="text-sm text-stone-500 capitalize">
                  {(event?.format || '').replace('_', ' ')}
                  {event?.language ? ` · ${event.language}` : ''}
                </p>
              </div>

              {sessions.length > 0 && (
                <ul className="space-y-2">
                  {sessions.map((s) => (
                    <li key={s.id} className="text-sm rounded-lg bg-stone-50 border border-stone-100 px-3 py-2">
                      <div className="font-medium">{s.title || 'Session'}</div>
                      <SessionTimes startsAt={s.starts_at} endsAt={s.ends_at} timezone={s.timezone} className="my-0.5" />
                      {s.location_text && <div className="text-stone-500">{s.location_text}</div>}
                    </li>
                  ))}
                </ul>
              )}

              {invite && event?.slug && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 pt-1">
                  <code className="text-xs bg-stone-100 rounded px-2 py-1.5 break-all flex-1">
                    {getPmEventPreferredLiveUrl(event, invite.slug)}
                  </code>
                  <button
                    type="button"
                    onClick={() => copyInvite(event, invite)}
                    className="h-9 px-3 rounded-lg bg-amber-800 text-white text-sm font-medium hover:bg-amber-700"
                  >
                    {copiedId === invite.id ? 'Copied' : 'Copy invite link'}
                  </button>
                </div>
              )}
            </article>
          ))}
        </section>

        <section className="rounded-2xl border border-dashed border-stone-300 bg-white/60 p-6">
          <h2 className="text-lg font-semibold mb-1">Resources</h2>
          <p className="text-sm text-stone-500">
            Event resources will appear here in a future update.
          </p>
        </section>
      </main>
    </div>
  );
}
